/**
 * audio/synth.js — the synthesis toolkit every sound effect is built from.
 *
 * There are no sample files in this game. Each helper here schedules Web Audio
 * nodes against an explicit `ctx` and an explicit start time `t0`, which is
 * what lets the exact same recipe run two ways:
 *
 *   - live, against the shared AudioContext, for the player;
 *   - offline, against an OfflineAudioContext, so a test can render a recipe to
 *     a buffer and assert it is genuinely audible.
 *
 * Because of that second path, **nothing in here may read `ctx.currentTime`
 * implicitly or touch module-level mutable audio state.** Pass `t0` in.
 *
 * Randomness comes from a private generator, never from `core/testmode.rng()`.
 * Sharing the gameplay stream would make audio pitch variation consume seeded
 * numbers and quietly break every other agent's determinism.
 *
 * @module audio/synth
 */

// ─────────────────────────────────────────────────────────────────────────────
// Private RNG — deliberately NOT the gameplay generator
// ─────────────────────────────────────────────────────────────────────────────

let _a = 0x9e3779b9;

/** @returns {number} [0, 1) */
export function arand() {
  _a = (_a + 0x6d2b79f5) >>> 0;
  let t = _a;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** @param {number} a @param {number} b @returns {number} */
export function arange(a, b) {
  return a + (b - a) * arand();
}

/** @template T @param {T[]} arr @returns {T} */
export function apick(arr) {
  return arr[Math.floor(arand() * arr.length) % arr.length];
}

/** Re-seed the audio generator so an offline render is byte-reproducible. */
export function aseed(seed = 0x9e3779b9) {
  _a = seed >>> 0;
}

/** Deterministic seed from a string, for per-track music generation. */
export function hashSeed(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A small independent generator, so music does not perturb SFX variation. */
export function makeRng(seed) {
  let s = seed >>> 0;
  return function next() {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Noise
// ─────────────────────────────────────────────────────────────────────────────

const NOISE_CACHE = new WeakMap();

/**
 * A looping noise buffer, memoised per context and colour.
 * @param {BaseAudioContext} ctx
 * @param {'white'|'pink'|'brown'} [color]
 * @param {number} [seconds]
 * @returns {AudioBuffer}
 */
export function noiseBuffer(ctx, color = 'white', seconds = 2) {
  let byColor = NOISE_CACHE.get(ctx);
  if (!byColor) { byColor = new Map(); NOISE_CACHE.set(ctx, byColor); }
  const key = `${color}:${seconds}`;
  const hit = byColor.get(key);
  if (hit) return hit;

  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  const rnd = makeRng(0xc0ffee ^ len);

  if (color === 'pink') {
    // Paul Kellett's economical pink filter.
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < len; i++) {
      const w = rnd() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
  } else if (color === 'brown') {
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = rnd() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    }
  } else {
    for (let i = 0; i < len; i++) d[i] = rnd() * 2 - 1;
  }
  byColor.set(key, buf);
  return buf;
}

// ─────────────────────────────────────────────────────────────────────────────
// Envelopes
// ─────────────────────────────────────────────────────────────────────────────

const FLOOR = 0.0001;

/**
 * Schedule a percussive attack/hold/decay on a gain param.
 *
 * Exponential decay is used because it is what the ear reads as "natural", but
 * an exponential ramp can never reach zero — it lands on FLOOR and is then cut.
 *
 * @param {AudioParam} param
 * @param {number} t0
 * @param {{ peak?: number, attack?: number, hold?: number, decay?: number,
 *           curve?: 'exp'|'lin' }} [opts]
 * @returns {number} The time the envelope finishes.
 */
export function adsr(param, t0, opts = {}) {
  const { peak = 0.3, attack = 0.004, hold = 0, decay = 0.2, curve = 'exp' } = opts;
  const aEnd = t0 + Math.max(0.0005, attack);
  const hEnd = aEnd + Math.max(0, hold);
  const end = hEnd + Math.max(0.005, decay);

  param.setValueAtTime(FLOOR, t0);
  param.exponentialRampToValueAtTime(Math.max(FLOOR, peak), aEnd);
  if (hold > 0) param.setValueAtTime(Math.max(FLOOR, peak), hEnd);
  if (curve === 'lin') param.linearRampToValueAtTime(0, end);
  else param.exponentialRampToValueAtTime(FLOOR, end);
  param.setValueAtTime(0, end);
  return end;
}

/**
 * A sustained envelope with an explicit release — for held sounds like hums.
 * @param {AudioParam} param
 * @param {number} t0
 * @param {{ peak?: number, attack?: number, sustain?: number, release?: number }} opts
 * @returns {number} End time.
 */
export function asr(param, t0, { peak = 0.2, attack = 0.05, sustain = 1, release = 0.3 } = {}) {
  const aEnd = t0 + attack;
  const sEnd = aEnd + Math.max(0, sustain);
  const end = sEnd + Math.max(0.01, release);
  param.setValueAtTime(FLOOR, t0);
  param.exponentialRampToValueAtTime(Math.max(FLOOR, peak), aEnd);
  param.setValueAtTime(Math.max(FLOOR, peak), sEnd);
  param.exponentialRampToValueAtTime(FLOOR, end);
  param.setValueAtTime(0, end);
  return end;
}

// ─────────────────────────────────────────────────────────────────────────────
// Panning + routing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A stereo panner, or a plain gain where StereoPannerNode is unavailable.
 * @param {BaseAudioContext} ctx
 * @param {number} x -1 (left) … 1 (right)
 * @returns {AudioNode}
 */
export function panNode(ctx, x = 0) {
  if (typeof ctx.createStereoPanner === 'function') {
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, x));
    return p;
  }
  return ctx.createGain();
}

/**
 * Gain → optional pan → destination, returned as one entry point.
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {number} pan
 * @returns {GainNode} Connect sources to this.
 */
export function out(ctx, dest, pan = 0) {
  const g = ctx.createGain();
  if (pan) {
    const p = panNode(ctx, pan);
    g.connect(p);
    p.connect(dest);
  } else {
    g.connect(dest);
  }
  return g;
}

/**
 * Connect a list of nodes in series.
 * @param {AudioNode[]} nodes
 * @returns {{ input: AudioNode, output: AudioNode }}
 */
export function chain(nodes) {
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
  return { input: nodes[0], output: nodes[nodes.length - 1] };
}

/**
 * A configured biquad.
 * @param {BaseAudioContext} ctx
 * @param {BiquadFilterType} type
 * @param {number} freq
 * @param {number} [q]
 * @returns {BiquadFilterNode}
 */
export function filter(ctx, type, freq, q = 1) {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = Math.max(10, Math.min(ctx.sampleRate / 2 - 100, freq));
  f.Q.value = q;
  return f;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sources
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single enveloped oscillator, optionally gliding in pitch.
 *
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {{ type?: OscillatorType, freq: number, freqTo?: number,
 *           glide?: number, detune?: number, t0: number, gain?: number,
 *           attack?: number, hold?: number, decay?: number, pan?: number,
 *           curve?: 'exp'|'lin' }} o
 * @returns {number} End time.
 */
export function osc(ctx, dest, o) {
  const {
    type = 'sine', freq, freqTo = null, glide = null, detune = 0, t0,
    gain = 0.3, attack = 0.004, hold = 0, decay = 0.25, pan = 0, curve = 'exp',
  } = o;

  const g = out(ctx, dest, pan);
  const n = ctx.createOscillator();
  n.type = type;
  n.detune.value = detune;
  n.frequency.setValueAtTime(Math.max(1, freq), t0);
  if (freqTo != null) {
    const at = t0 + (glide == null ? Math.max(0.01, attack + hold + decay) : glide);
    n.frequency.exponentialRampToValueAtTime(Math.max(1, freqTo), at);
  }
  n.connect(g);

  const end = adsr(g.gain, t0, { peak: gain, attack, hold, decay, curve });
  n.start(t0);
  n.stop(end + 0.02);
  return end;
}

/**
 * A burst of filtered noise — the backbone of every impact and whoosh.
 *
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {{ color?: 'white'|'pink'|'brown', t0: number, gain?: number,
 *           attack?: number, hold?: number, decay?: number, pan?: number,
 *           type?: BiquadFilterType, freq?: number, freqTo?: number,
 *           q?: number, curve?: 'exp'|'lin', rate?: number }} o
 * @returns {number} End time.
 */
export function noiseBurst(ctx, dest, o) {
  const {
    color = 'white', t0, gain = 0.3, attack = 0.003, hold = 0, decay = 0.2,
    pan = 0, type = null, freq = 1000, freqTo = null, q = 1,
    curve = 'exp', rate = 1,
  } = o;

  const g = out(ctx, dest, pan);
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, color);
  src.loop = true;
  src.playbackRate.value = rate;
  // Start at a varying offset so repeated bursts are not identical.
  const off = arand() * 1.5;

  let tail = g;
  if (type) {
    const f = filter(ctx, type, freq, q);
    if (freqTo != null) {
      f.frequency.setValueAtTime(Math.max(10, freq), t0);
      f.frequency.exponentialRampToValueAtTime(
        Math.max(10, Math.min(ctx.sampleRate / 2 - 100, freqTo)),
        t0 + Math.max(0.02, attack + hold + decay),
      );
    }
    src.connect(f);
    f.connect(g);
    tail = f;
  } else {
    src.connect(g);
  }
  void tail;

  const end = adsr(g.gain, t0, { peak: gain, attack, hold, decay, curve });
  src.start(t0, off);
  src.stop(end + 0.02);
  return end;
}

/**
 * Two-operator FM — bells, clangs and the high striker.
 *
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {{ carrier: number, ratio?: number, index?: number, indexTo?: number,
 *           t0: number, gain?: number, attack?: number, decay?: number,
 *           pan?: number, type?: OscillatorType }} o
 * @returns {number} End time.
 */
export function fm(ctx, dest, o) {
  const {
    carrier, ratio = 2.0, index = 300, indexTo = 0, t0,
    gain = 0.25, attack = 0.004, decay = 1.2, pan = 0, type = 'sine',
  } = o;

  const g = out(ctx, dest, pan);
  const car = ctx.createOscillator();
  car.type = type;
  car.frequency.value = Math.max(1, carrier);

  const mod = ctx.createOscillator();
  mod.type = 'sine';
  mod.frequency.value = Math.max(1, carrier * ratio);

  const modGain = ctx.createGain();
  modGain.gain.setValueAtTime(index, t0);
  modGain.gain.exponentialRampToValueAtTime(Math.max(FLOOR, indexTo || 0.01), t0 + decay);
  mod.connect(modGain);
  modGain.connect(car.frequency);
  car.connect(g);

  const end = adsr(g.gain, t0, { peak: gain, attack, decay });
  mod.start(t0); car.start(t0);
  mod.stop(end + 0.02); car.stop(end + 0.02);
  return end;
}

/**
 * Karplus–Strong style pluck: a noise seed circulating in a damped delay line.
 * Cheap and works identically offline, which a ScriptProcessor would not.
 *
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {{ freq: number, t0: number, gain?: number, decay?: number,
 *           damp?: number, pan?: number }} o
 * @returns {number} End time.
 */
export function pluck(ctx, dest, o) {
  const { freq, t0, gain = 0.3, decay = 0.5, damp = 3000, pan = 0 } = o;
  const g = out(ctx, dest, pan);

  const period = 1 / Math.max(20, freq);
  const delay = ctx.createDelay(0.2);
  delay.delayTime.value = Math.min(0.19, period);

  const fb = ctx.createGain();
  // Feedback set so the line loses ~60 dB over `decay` seconds.
  fb.gain.value = Math.min(0.995, Math.pow(0.001, period / Math.max(0.05, decay)));
  const lp = filter(ctx, 'lowpass', damp, 0.7);

  delay.connect(lp);
  lp.connect(fb);
  fb.connect(delay);
  delay.connect(g);

  const seed = ctx.createBufferSource();
  seed.buffer = noiseBuffer(ctx, 'white');
  seed.loop = true;
  const seedGain = ctx.createGain();
  seed.connect(seedGain);
  seedGain.connect(delay);
  adsr(seedGain.gain, t0, { peak: 1, attack: 0.0005, decay: Math.max(0.002, period * 2) });

  const end = adsr(g.gain, t0, { peak: gain, attack: 0.002, hold: decay * 0.5, decay: decay * 0.6 });
  seed.start(t0, arand());
  seed.stop(t0 + period * 4 + 0.02);
  return end;
}

// ─────────────────────────────────────────────────────────────────────────────
// Effects — wet-only sends, the caller mixes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A cheap Schroeder reverb: parallel damped combs into two allpass stages.
 * @param {BaseAudioContext} ctx
 * @param {{ decay?: number, damp?: number }} [opts]
 * @returns {{ input: AudioNode, output: AudioNode }} 100% wet.
 */
export function reverb(ctx, { decay = 1.4, damp = 3400 } = {}) {
  const input = ctx.createGain();
  const merge = ctx.createGain();
  merge.gain.value = 0.32;

  for (const time of [0.0297, 0.0371, 0.0411, 0.0437]) {
    const d = ctx.createDelay(0.5);
    d.delayTime.value = time;
    const fb = ctx.createGain();
    fb.gain.value = Math.min(0.92, Math.pow(0.001, time / Math.max(0.1, decay)));
    const lp = filter(ctx, 'lowpass', damp, 0.5);
    input.connect(d);
    d.connect(lp);
    lp.connect(fb);
    fb.connect(d);
    d.connect(merge);
  }

  const ap1 = filter(ctx, 'allpass', 480, 0.6);
  const ap2 = filter(ctx, 'allpass', 1700, 0.6);
  merge.connect(ap1);
  ap1.connect(ap2);
  return { input, output: ap2 };
}

/**
 * A damped feedback delay.
 * @param {BaseAudioContext} ctx
 * @param {{ time?: number, feedback?: number, damp?: number }} [opts]
 * @returns {{ input: AudioNode, output: AudioNode }} 100% wet.
 */
export function feedbackDelay(ctx, { time = 0.18, feedback = 0.32, damp = 4200 } = {}) {
  const input = ctx.createGain();
  const d = ctx.createDelay(1.5);
  d.delayTime.value = Math.min(1.4, time);
  const fb = ctx.createGain();
  fb.gain.value = Math.min(0.85, feedback);
  const lp = filter(ctx, 'lowpass', damp, 0.5);
  input.connect(d);
  d.connect(lp);
  lp.connect(fb);
  fb.connect(d);
  return { input, output: lp };
}

/**
 * Route `src` into an effect at `amount`, mixing back into `dest`.
 *
 * A reverb or delay is a feedback loop: left connected it never truly stops, so
 * every send takes the time its tail should be gone by. The wet path is faded
 * out there and torn down shortly after, which is what keeps a session's worth
 * of fanfares from accumulating live node graphs.
 *
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} src
 * @param {{ input: AudioNode, output: AudioNode }} effect
 * @param {number} amount
 * @param {AudioNode} dest
 * @param {number} [until] Absolute time the tail should be silent by.
 */
export function send(ctx, src, effect, amount, dest, until) {
  const g = ctx.createGain();
  g.gain.value = amount;
  src.connect(g);
  g.connect(effect.input);

  const wet = ctx.createGain();
  wet.gain.value = 1;
  effect.output.connect(wet);
  wet.connect(dest);

  if (until != null) {
    wet.gain.setValueAtTime(1, Math.max(0, until - 0.25));
    wet.gain.linearRampToValueAtTime(0, until);
    const ms = Math.max(0, (until - ctx.currentTime) * 1000) + 250;
    if (typeof setTimeout === 'function') {
      setTimeout(() => {
        try { g.disconnect(); wet.disconnect(); effect.output.disconnect(); } catch { /* gone */ }
      }, Math.min(30_000, ms));
    }
  }
}

/**
 * A soft-clip waveshaper.
 * @param {BaseAudioContext} ctx
 * @param {number} [amount] 0 (clean) … 100 (hard)
 * @returns {WaveShaperNode}
 */
export function distortion(ctx, amount = 18) {
  const ws = ctx.createWaveShaper();
  const n = 2048;
  const curve = new Float32Array(n);
  const k = Math.max(0.001, amount);
  for (let i = 0; i < n; i++) {
    const x = (i * 2) / n - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}

/**
 * A slow LFO wired into an AudioParam — used for ambient filter movement.
 * @param {BaseAudioContext} ctx
 * @param {AudioParam} param
 * @param {{ rate?: number, depth?: number, t0?: number, type?: OscillatorType }} o
 * @returns {OscillatorNode} Already started; call `.stop()` to end it.
 */
export function lfo(ctx, param, { rate = 0.1, depth = 200, t0 = 0, type = 'sine' } = {}) {
  const n = ctx.createOscillator();
  n.type = type;
  n.frequency.value = rate;
  const g = ctx.createGain();
  g.gain.value = depth;
  n.connect(g);
  g.connect(param);
  n.start(t0);
  return n;
}

/**
 * MIDI note number → Hz. Music and fanfares are written in note numbers.
 * @param {number} n 69 = A4 = 440 Hz
 * @returns {number}
 */
export function mtof(n) {
  return 440 * Math.pow(2, (n - 69) / 12);
}
